import { EMU, initEnvironment } from '@/globals/typescript/utils/host'
import { initializeApp, type FirebaseApp } from 'firebase/app'
import {
  connectStorageEmulator,
  getStorage,
  type FirebaseStorage,
} from 'firebase/storage'
import {
  initializeAuth,
  browserLocalPersistence,
  type Auth,
} from 'firebase/auth'
import {
  connectFirestoreEmulator,
  getFirestore,
  type Firestore,
} from 'firebase/firestore'

// Optional imports – uncomment as needed
// import { getFunctions } from 'firebase/functions'

/**
 * Firebase services — live ES module bindings, assigned by initFirebase()
 * during bootstrap before anything dereferences them.
 */
export let app: FirebaseApp
export let auth: Auth
export let db: Firestore
export let storage: FirebaseStorage
// export let functions: Functions

/**
 * Initialize Firebase from the environment's own config. Firebase Hosting
 * serves the reserved /__/firebase/init.json in every deployed environment;
 * locally the Vite dev server proxies it to the dev project.
 */
export async function initFirebase(): Promise<void> {
  const response = await fetch('/__/firebase/init.json').catch(
    (err: unknown) => {
      throw new Error(`Failed to fetch /__/firebase/init.json: ${String(err)}`)
    }
  )
  if (!response.ok) {
    throw new Error(
      `Failed to fetch /__/firebase/init.json: ${response.status} ${response.statusText}`
    )
  }
  const firebaseConfig = (await response.json()) as { projectId: string }

  initEnvironment(firebaseConfig.projectId)

  app = initializeApp(firebaseConfig)

  // browserLocalPersistence stores auth state in localStorage (not IndexedDB).
  // This is required for Playwright's storageState() to capture the session.
  auth = initializeAuth(app, { persistence: browserLocalPersistence })
  db = getFirestore(app)

  if (EMU.isEmulatingFirestore) {
    connectFirestoreEmulator(db, 'localhost', 8080)
  }

  storage = getStorage(app)
  if (EMU.isEmulatingStorage) {
    connectStorageEmulator(storage, 'localhost', 9199)
  }
  // functions = getFunctions(app)
}
